import { makeStyles, DisplayValue, DisplayType, VerticalAlignment, MetadataField, ModelRow, StringUtil, HorizontalAlignment } from "@mcleod/core";
import { Panel } from "../panel/Panel";
import { Table } from "./Table";
import { TableCellProps, TableCellPropDefinitions } from "./TableCellProps";
import { TableColumn } from "./TableColumn";
import { TableRow, TableRowMode } from "./TableRow";
import { PropType } from "../../base/PropType";
import { DataSource } from "../../databinding/DataSource";
import { ComponentTypes } from "../../base/ComponentTypes";
import { Component } from "../..";
import { ComponentPropDefinition } from "../../base/ComponentProps";
import { SortSelector } from "./SortSelector";

const classes = makeStyles("td", {
  base: { verticalAlign: "top", paddingTop: "4px", paddingBottom: "4px", display: "table-cell", overflow: "hidden" },
});

export class TableCell extends Panel implements TableCellProps {
  public _table: Table;
  public _col: TableColumn;
  private _isHeading: boolean;
  private _caption: string;
  public displayType: DisplayType;
  public format: string;
  public row: TableRow;

  constructor(props: Partial<TableCellProps>) {
    super(props);
    this.setProps(props);
  }

  override setProps(props: Partial<TableCellProps>) {
    super.setProps(props);
  }

  get table(): Table {
    return this._table;
  }

  set table(value: Table) {
    this._table = value;
    (this as any)._parent = this.table;
    this.table._applyCellProps(this);
  }

  get col(): TableColumn {
    return this._col;
  }

  set col(value: TableColumn) {
    this._col = value;
    this.width = value?.heading?.width;
  }

  override _addDefaultClassName() {
    this.element.classList.add(classes.base);
  }

  override displayData(data: ModelRow, allData: ModelRow[], rowIndex: number) {
    this.fireDataDisplayListeners(data, allData, rowIndex);

    if (this._designer != null)
      return;
    for (const component of this.components) {
      if (component.displayData != null)
        component.displayData(data, allData, rowIndex);
    }

    //if field has been specified and no components are present, show value from field in cell's caption
    //this shouldn't happen anymore in non-designer screens due to populateSimpleComponent()
    if (this.field != null && this.components.length == 0) {
      if (data == null)
        this.caption = null;
      else if(data != null){
        if (data instanceof ModelRow) // this *should* always be true but isn't while PropertiesTable adds rows with raw data to the Tabel
          this.caption = DisplayValue.getDisplayValue(data.get(this.field), this.displayType, this.format);
        else
          this.caption = DisplayValue.getDisplayValue(data[this.field], this.displayType, this.format);
      }
    }
  }

  private matchHeadingAlignment() {
    if (this.isHeading && this.components.length > 0 && this.align != null)
      this.components[0].align = this.align;
  }

  override set align(value: HorizontalAlignment) {
    super.align = value;
    this.matchHeadingAlignment();
  }

  override get align(): HorizontalAlignment {
    return super.align;
  }

  override add(...components: Component[]): Component {
    const result = super.add(...components);
    this.matchHeadingAlignment();
    return result;
  }

  override get components(): Component[] {
    return super.components;
  }

  override set components(value: Component[]) {
    super.components = value;
    this.matchHeadingAlignment();
  }

  get isHeading() {
    return this._isHeading;
  }

  set isHeading(value) {
    this._isHeading = value;
    if (value === true) {
      this.allowSelect = false;
      this._element.classList.add(classes.heading);
    }
  }

  get caption(): string {
    return this._caption;
  }

  set caption(value: string) {
    const oldValue = this._caption;
    this._caption = value;
    if (value == null)
      this._element.innerText = "";
    else
      this._element.innerText = value;
    this._matchIdToValue(oldValue, value, this._isHeading !== true ? this._getComponentTypeName() : "columnHeader");
  }

  get verticalAlign() {
    return super.verticalAlign;
  }

  set verticalAlign(value) {  // does this work without this getter/setter????  Panel does something similar.
    super.verticalAlign = value;
    if (value === VerticalAlignment.CENTER)
      this._element.style.verticalAlign = "middle";
    else if (value === VerticalAlignment.BOTTOM)
      this._element.style.verticalAlign = "bottom";
    else
      this._element.style.verticalAlign = "";
  }

  override getPropertyDefinitions() {
    return TableCellPropDefinitions.getDefinitions();
  }

  override _deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, componentCreationCallback): string[] {
    //if cell doesn't define any components and a dataSource field is defined, create simple component in the component def
    //create textbox if cell is editable, label if not
    //(don't do this in the designer)
    if (this._designer == null) {
      const field = compDef.field;
      const dataSource = this.table.dataSource;
      this.populateSimpleComponent(compDef, field, dataSource);
    }

    const result = super._deserializeSpecialProps(componentOwner, compDef, defaultPropValues, dataSources, componentCreationCallback);
    if (this._designer == null) {
      // this code (and the related block below that is also commented out) was used to save changes on the blur of every component
      // let addlCompListeners = null;
      // if (this.table.rowModeControlType === RowModeControlType.AUTO) {
      //   addlCompListeners = {};
      //   addlCompListeners[ComponentListenerDefs.blur.domEventName] = {
      //     def: ComponentListenerDefs.blur,
      //     listeners: [
      //       (event: BlurEvent) => this.row.autoSave(event)
      //     ]
      //   }
      // }

      const initiallyPrintable = this.row.mode === TableRowMode.NONE;
      for (const component of this.components) {
        component.insideTableCell = true;
        if (this.row.boundRow != null) {
          this.forEveryChildComponent((component: Component) => {
            if (component.boundRow == null)
              component.boundRow = this.row.boundRow;
          });
        }
        if (this.row.mode !== TableRowMode.QUICK_ADD) {
          if (component["printable"] != null)
            component["printable"] = initiallyPrintable;
          // if (addlCompListeners != null) {
          //   component.addListenersToAll(addlCompListeners);
          // }
        }
        else {
          if (component["printable"] != null) {
            component["printable"] = false;
          }
        }
      }
      this.setPropOnChildren("allowIndeterminate", this.row.mode === TableRowMode.SEARCH);
    }
    return result;
  }

  override _serializeNonProps():string {
    if (this.isHeading)
      return "";
    else
      return super._serializeNonProps();
  }

  override getPropertyDefaultValue(prop: ComponentPropDefinition): any {
    if (prop.name === "paddingLeft" && this._isFirstCellInRow() === true)
      return 12;
      if (prop.name === "paddingRight" && this._isLastCellInRow() === true)
      return 12;
    return super.getPropertyDefaultValue(prop);
  }

  private _isFirstCellInRow(): boolean {
    return this.row.isFirstCellInRow(this);
  }

  private _isLastCellInRow(): boolean {
    return this.row.isLastCellInRow(this);
  }

  populateSimpleComponent(compDef: any, field: string, dataSource: DataSource) {
    if (compDef.components == null && field != null && dataSource != null) {
      const md = dataSource.getMetadataFromCache();
      const mdField: MetadataField = md?.output?.[field];
      if (mdField != null) {
        compDef.components = [];
        const commonProps = { field, displayType: mdField.displayType, caption: mdField.caption, id: "cell" + field, required: mdField.required, fillRow: true };
        if (this.row.canBeEdited) {
          if (mdField.dataType !== (PropType.bool as string))
            compDef.components.push({ ...commonProps, type: "textbox" });
          else
            compDef.components.push({ ...commonProps, type: "checkbox" });
        }
        else
          compDef.components.push({ ...commonProps, type: "label", caption: undefined });
      }
    }
  }

  override get serializationName() {
    return "cell";
  }
}

ComponentTypes.registerComponentType("cell", TableCell.prototype.constructor);
